import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { NAV_ITEMS } from "./NavbarMenuConfig";

function MobileLabel({ to, children, onNavigate }) {
  if (to) {
    return (
      <Link to={to} onClick={onNavigate}>
        {children}
      </Link>
    );
  }

  return (
    <a href="#nav" onClick={(e) => e.preventDefault()}>
      {children}
    </a>
  );
}

function MobileMenuItems({ items, t, openMap, toggle, onNavigate }) {
  return items.map((item) => {
    const hasChildren =
      Array.isArray(item.children) && item.children.length > 0;
    const isOpen = !!openMap[item.id];

    const liClass = hasChildren ? "has-sub" : item.mobileLiClass || undefined;

    return (
      <li key={item.id} className={liClass}>
        <MobileLabel to={item.to} onNavigate={onNavigate}>
          {t(item.labelKey)}
        </MobileLabel>

        {hasChildren && isOpen ? (
          <ul className="mobile-drawer__submenu" style={{ display: "block" }}>
            {item.mobileSubmenuLink ? (
              <li>
                <Link to={item.mobileSubmenuLink.to} onClick={onNavigate}>
                  {t(item.mobileSubmenuLink.labelKey)}
                </Link>
              </li>
            ) : null}

            <MobileMenuItems
              items={item.children}
              t={t}
              openMap={openMap}
              toggle={toggle}
              onNavigate={onNavigate}
            />
          </ul>
        ) : null}

        {hasChildren ? (
          <button
            type="button"
            className={`mean-expand mobile-drawer__expand ${isOpen ? "is-open" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              toggle(item.id);
            }}
            aria-label={isOpen ? "Collapse submenu" : "Expand submenu"}
          >
            {isOpen ? "−" : "+"}
          </button>
        ) : null}
      </li>
    );
  });
}

function MobileNav({ t, currentLanguage, changeLanguage }) {
  const [mobile, setMobile] = useState(false);
  const [openMap, setOpenMap] = useState({});

  const logoSrc =
    currentLanguage === "me"
      ? "/assets/img/logo/logo.png"
      : "/assets/img/logo/logoENG.png";

  const closeMenu = useCallback(() => {
    setMobile(false);
    setOpenMap({});
  }, []);

  const toggle = useCallback((id) => {
    setOpenMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // lock body scroll
  useEffect(() => {
    if (mobile) document.body.classList.add("mobileNavOpen");
    else document.body.classList.remove("mobileNavOpen");

    return () => document.body.classList.remove("mobileNavOpen");
  }, [mobile]);

  // ESC close (only when open)
  useEffect(() => {
    if (!mobile) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobile, closeMenu]);

  return (
    <div className="col-12">
      {/* is-open na container: CSS može pouzdano da kontroliše drawer */}
      <div className={`mobile-menu mean-container ${mobile ? "is-open" : ""}`}>
        {/* Toggle button (hamburger) */}
        <button
          type="button"
          className={`meanmenu-reveal ${mobile ? "meanclose" : ""}`}
          onClick={() => setMobile((p) => !p)}
          aria-label={mobile ? "Close menu" : "Open menu"}
          aria-expanded={mobile}
        >
          {mobile ? (
            <span className="mobile-drawer__x">×</span>
          ) : (
            <span className="mobile-drawer__burger" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          )}
        </button>

        {/* Backdrop (dodao i mobile-nav-backdrop kao “alias” klasu) */}
        <div
          className={`mobile-drawer__backdrop mobile-nav-backdrop ${mobile ? "is-open" : ""}`}
          onClick={closeMenu}
          aria-hidden={!mobile}
        />

        {/* Drawer */}
        <nav
          className={`mean-nav mobile-drawer ${mobile ? "is-open" : ""}`}
          aria-hidden={!mobile}
        >
          <div className="mobile-drawer__header">
            <Link
              to="/"
              className="mobile-drawer__brand"
              onClick={closeMenu}
              aria-label="Home"
            >
              <img src={logoSrc} alt="FPEP" />
            </Link>

            <button
              type="button"
              className="mobile-drawer__close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          <ul className="mobile-drawer__menu" style={{ display: "block" }}>
            <MobileMenuItems
              items={NAV_ITEMS}
              t={t}
              openMap={openMap}
              toggle={toggle}
              onNavigate={closeMenu}
            />

            {/* Social */}
            <li className="mobile-social">
              <div className="mobile-social-title">{t("navbar.followUs")}</div>

              <div className="mobile-social-links">
                <a
                  href="https://www.facebook.com/fpebar.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  title="Facebook"
                >
                  <i className="fab fa-facebook-f" />
                </a>
                <a
                  href="https://www.instagram.com/fpepbar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  title="Instagram"
                >
                  <i className="fab fa-instagram" />
                </a>
                <a
                  href="https://www.youtube.com/@fpepbar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  title="YouTube"
                >
                  <i className="fab fa-youtube" />
                </a>
                <a
                  href="https://www.tiktok.com/@fpepbar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  title="TikTok"
                >
                  <i className="fab fa-tiktok" />
                </a>
                <a
                  href="https://www.linkedin.com/school/fpepbar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                >
                  <i className="fab fa-linkedin-in" />
                </a>
              </div>
            </li>

            {/* Language */}
            <li className="mobile-drawer__lang">
              {currentLanguage === "en" ? (
                <button
                  onClick={() => changeLanguage("me")}
                  className="language-button"
                >
                  <span className="flag-icon flag-icon-me" />
                  <span className="mobile-drawer__langText">ME</span>
                </button>
              ) : (
                <button
                  onClick={() => changeLanguage("en")}
                  className="language-button"
                >
                  <span className="flag-icon flag-icon-gb" />
                  <span className="mobile-drawer__langText">EN</span>
                </button>
              )}
            </li>

            {/* Contact card */}
            <li className="mobile-drawer__contact">
              <a href="tel:+38268011311" onClick={closeMenu}>
                <i className="fas fa-phone-alt" />
                <span>
                  <small>{t("navbar.CallUs")}</small>
                  <strong>+382 68 003113</strong>
                </span>
              </a>

              <a href="mailto:fpebar@gmail.com" onClick={closeMenu}>
                <i className="fas fa-envelope" />
                <span>
                  <small>{t("navbar.Email")}</small>
                  <strong>fpebar@gmail.com</strong>
                </span>
              </a>

              <a
                href="https://e-fpep.com/login/index.php?lang=sr_lt"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-user-graduate" />
                <span>
                  <small>{t("navbar.StudentPortal")}</small>
                  <strong>{t("navbar.Login")}</strong>
                </span>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}

export default MobileNav;
